import { ActionCard, HeadlessButton, VisuallyHidden } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import styled from 'styled-components';

interface Props {
	title: string;
	description?: string;
	scheme: string;
	onClick: () => void;
}

export function AuthMethodLink(props: Props): JSX.Element {
	return (
		<>
			{props.scheme.toLocaleLowerCase() === 'nhs' ? (
				<StyledNhsButtonOuter onClick={props.onClick} data-testid={`auth-method-link-${props.scheme}`}>
					<VisuallyHidden>{props.title}</VisuallyHidden>
					{props.description ? <VisuallyHidden>{props.description}</VisuallyHidden> : null}

					<NhsButton />
				</StyledNhsButtonOuter>
			) : (
				<ActionCard.Root
					data-testid={`auth-method-link-${props.scheme}`}
					icon={<SchemeLogo scheme={props.scheme} />}
					onClick={props.onClick}
				>
					<ActionCard.Title>{props.title}</ActionCard.Title>
					{props.description != null ? <ActionCard.Meta>{props.description}</ActionCard.Meta> : null}
				</ActionCard.Root>
			)}
		</>
	);
}

const StyledNhsButtonOuter = styled(HeadlessButton)`
	display: flex;
	width: 100%;
	height: 64px;
	position: relative;
	border: unset;
	background-color: #003087;
	border-radius: 4px;
	padding: 0 4px;
	align-items: center;

	&:before {
		content: '';
		height: 60px;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		background-color: #005eb8;
		border-radius: 4px;
	}

	&:hover {
		&:before {
			background-color: #1e569b;
		}

		svg .bg {
			fill: #1e569b;
		}
	}

	&:focus-visible {
		outline: 2px solid ${tokens.color.focus};
		outline-offset: 2px;
	}

	svg {
		position: absolute;
		max-width: 100%;
		max-height: 100%;
	}
`;

function NhsButton(): JSX.Element {
	return (
		<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320.22 59'>
			<rect fill='#005eb8' className='bg' width='320.22' height='55' rx='0' />
			<rect fill='#005eb8' className='bg' x='2' y='2' width='317.84' height='51' />
			<rect fill='#005eb8' className='bg' x='101.05' y='14' width='200.79' height='27' />
			<path
				fill='white'
				d='M112.38,33.89a13.83,13.83,0,0,1-3.06.42c-4.6,0-7.37-2.38-7.37-6.71,0-4.54,2.76-7.18,7.37-7.18a8.78,8.78,0,0,1,3,.56l-.19,2.27a5.51,5.51,0,0,0-2.9-.71c-2.72,0-4.51,2.06-4.51,4.87s1.77,4.79,4.56,4.79a7.6,7.6,0,0,0,3-.56Z'
			/>
			<path
				fill='white'
				d='M118.68,34.31a4.89,4.89,0,0,1-5.18-5.12,5.19,5.19,0,1,1,10.37,0A4.89,4.89,0,0,1,118.68,34.31Zm2.5-5.42c0-1.58-.85-2.95-2.5-2.95s-2.48,1.39-2.48,2.95c0,2.09.8,3.42,2.48,3.42S121.18,31,121.18,28.89Z'
			/>
			<path
				fill='white'
				d='M132.42,34.08V28.52c0-1.48-.31-2.58-1.67-2.58-1.64,0-2.27,1.58-2.27,3.33v4.81H125.9V24.17h2.44v1.35h0a3.7,3.7,0,0,1,3.25-1.58c2.27,0,3.37,1.56,3.37,3.89v6.25Z'
			/>
			<path
				fill='white'
				d='M143.55,34.08a6.56,6.56,0,0,1-1.89.23,2.92,2.92,0,0,1-3.16-3.21v-5h-1.9V24.17h1.9v-2l2.58-.83v2.81h2.29v1.89h-2.29v4.6c0,1,.33,1.65,1.23,1.65a2.21,2.21,0,0,0,1.16-.27Z'
			/>
			<path fill='white' d='M145.2,22.36V19.9h2.58v2.46Zm0,11.72V24.17h2.58v9.91Z' />
			<path
				fill='white'
				d='M157,34.08V28.52c0-1.48-.31-2.58-1.67-2.58-1.64,0-2.28,1.58-2.28,3.33v4.81H150.5V24.17h2.44v1.35h0a3.69,3.69,0,0,1,3.25-1.58c2.27,0,3.37,1.56,3.37,3.89v6.25Z'
			/>
			<path
				fill='white'
				d='M168.91,34.08V32.74h0a4.14,4.14,0,0,1-3.26,1.57c-2.21,0-3.36-1.48-3.36-3.88V24.17h2.58v5.66c0,2,.73,2.48,1.67,2.48,1.56,0,2.27-1.44,2.27-3.35V24.17h2.58v9.91Z'
			/>
			<path
				fill='white'
				d='M182,33.52a6.76,6.76,0,0,1-3.38.79c-3.29,0-5.2-1.88-5.2-5.13,0-2.78,1.45-5.24,4.6-5.24,3.37,0,4.72,2.08,4.72,6h-6.85a2.58,2.58,0,0,0,2.84,2.5,6.66,6.66,0,0,0,3.27-1Zm-1.73-5.36c-.09-1.43-.77-2.33-2.11-2.33a2.23,2.23,0,0,0-2.27,2.33Z'
			/>
			<path
				fill='white'
				d='M196,34.08a6.56,6.56,0,0,1-1.89.23,2.91,2.91,0,0,1-3.15-3.21v-5H189V24.17h1.91v-2l2.58-.83v2.81h2.28v1.89H193.5v4.6c0,1,.32,1.65,1.23,1.65a2.2,2.2,0,0,0,1.15-.27Z'
			/>
			<path
				fill='white'
				d='M202.1,34.31a4.89,4.89,0,0,1-5.18-5.12,5.19,5.19,0,1,1,10.37,0A4.89,4.89,0,0,1,202.1,34.31Zm2.5-5.42c0-1.58-.85-2.95-2.5-2.95s-2.48,1.39-2.48,2.95c0,2.09.8,3.42,2.48,3.42S204.6,31,204.6,28.89Z'
			/>
			<path fill='white' d='M222.52,34.08l-5.14-10h0v10h-2.57V20.65h3.3l5.12,10.05h0v-10h2.58V34.08Z' />
			<path fill='white' d='M236.88,34.08V28.19h-5.39v5.89H228.8V20.65h2.69v5.43h5.39V20.65h2.69V34.08Z' />
			<path
				fill='white'
				d='M242.71,31.37a6.49,6.49,0,0,0,3,.83c1,0,2.39-.5,2.39-1.73,0-1-.91-1.47-2.23-2-1.79-.69-3.66-1.44-3.66-4,0-2.89,2.17-4.1,4.71-4.1a10,10,0,0,1,3.2.48l-.25,2.21a7.07,7.07,0,0,0-2.73-.57,1.87,1.87,0,0,0-2.12,1.86c0,1,1.15,1.41,2.27,1.85,1.81.67,3.62,1.5,3.62,4s-1.85,4.06-5.06,4.06a11.82,11.82,0,0,1-3.43-.52Z'
			/>
			<path fill='white' d='M258.57,34.08V19.65h2.58V34.08Z' />
			<path
				fill='white'
				d='M268.41,34.31a4.89,4.89,0,0,1-5.18-5.12,5.19,5.19,0,1,1,10.37,0A4.89,4.89,0,0,1,268.41,34.31Zm2.5-5.42c0-1.58-.85-2.95-2.5-2.95s-2.48,1.39-2.48,2.95c0,2.09.8,3.42,2.48,3.42S270.91,31,270.91,28.89Z'
			/>
			<path
				fill='white'
				d='M282.15,32.5h0A3.53,3.53,0,0,1,279,34.08c-2.73,0-4-2.08-4-5,0-2.39,1.23-5.14,4.2-5.14a3.3,3.3,0,0,1,3,1.58h0V24.17h2.47v9c0,3.35-1.73,5.19-5.2,5.19a8.68,8.68,0,0,1-3.46-.67l.21-2.19a7.3,7.3,0,0,0,2.91.86c2.42,0,3-1.56,3-3.61Zm0-3.48c0-1.52-.54-3.08-2.18-3.08s-2.28,1.33-2.28,3.14c0,1.35.71,3,2.19,3C281.53,32.08,282.15,30.62,282.15,29Z'
			/>
			<path fill='white' d='M287.44,22.36V19.9H290v2.46Zm0,11.72V24.17H290v9.91Z' />
			<path
				fill='white'
				d='M299.26,34.08V28.52c0-1.48-.31-2.58-1.67-2.58-1.64,0-2.27,1.58-2.27,3.33v4.81h-2.58V24.17h2.44v1.35h0a3.7,3.7,0,0,1,3.25-1.58c2.27,0,3.37,1.56,3.37,3.89v6.25Z'
			/>
			<polygon fill='#005eb8' points='18.54 14.54 18.54 40.46 84.42 40.46 84.42 14.54 18.54 14.54 18.54 14.54' />
			<path
				fill='white'
				d='M85.05,41V14H18V41ZM45,16.61,40.33,38.34H33.07l-4.57-15h-.06l-3,15H19.89l4.67-21.73h7.28l4.48,15.07h.06l3.07-15.07Zm20.77,0L61.2,38.34H55.35L57.29,29H50.37l-1.94,9.31H42.58l4.54-21.73H53l-1.72,8.31h6.91l1.72-8.31Zm16.81.59-1.41,4.33a11,11,0,0,0-4.82-1c-2.31,0-4.19.34-4.19,2.09,0,3.08,8.48,1.93,8.48,8.53,0,6-5.6,7.56-10.67,7.56a24.58,24.58,0,0,1-6.76-1.12l1.38-4.42a11.37,11.37,0,0,0,5.38,1.25c1.81,0,4.66-.35,4.66-2.59,0-3.49-8.48-2.18-8.48-8.31,0-5.61,4.94-7.29,9.73-7.29a17.91,17.91,0,0,1,6.7,1Z'
			/>
		</svg>
	);
}

interface LogoProps {
	scheme: string;
}

const StyledSchemeLogo = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	inset: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: ${tokens.spacing.x1_5};
`;

function SchemeLogo(props: LogoProps): JSX.Element {
	const scheme = props.scheme.toLocaleLowerCase();

	let icon: JSX.Element;
	switch (scheme) {
		case 'bankid':
			icon = <BankIdLogo />;
			break;
		case 'sms':
			icon = <SmsLogo />;
			break;
		case 'freja':
			icon = <FrejaEId />;
			break;
		case 'siths':
			icon = <SithsLogo />;
			break;
		case 'clientpwd1':
			icon = <ClientPwdLogo />;
			break;
		default:
			icon = <PadLockIcon />;
			break;
	}

	let background: string;
	switch (scheme) {
		case 'bankid':
			background = '#183E4F';
			break;
		case 'siths':
			background = '#00706E';
			break;
		case 'freja':
			background = 'linear-gradient(90deg, #3E3A94 0%, #1B7BCD 100%)';
			break;
		default:
			background = tokens.color.bg.surfaceBrand;
			break;
	}

	return (
		<StyledSchemeLogo
			style={{
				background,
			}}
		>
			{icon}
		</StyledSchemeLogo>
	);
}

function FrejaEId(): JSX.Element {
	return (
		<svg xmlns='http://www.w3.org/2000/svg' width='32' height='8' viewBox='0 0 32 8' fill='none'>
			<path
				d='M23.2054 7.41595L24.1134 5.45693C24.1219 5.44012 24.1219 5.4233 24.1219 5.40649V0.32819C24.1219 0.235704 24.0462 0.160034 23.9537 0.160034H21.1875C21.1371 0.160034 21.0866 0.193665 21.0698 0.244112L20.5906 1.40439C20.557 1.48846 20.6158 1.58095 20.7083 1.58095H22.3899C22.4823 1.58095 22.558 1.65662 22.558 1.7491V5.39808C22.558 5.86892 22.2385 6.33975 21.65 6.3902C21.0782 6.43224 20.4813 6.01185 20.1282 5.70917C20.0693 5.64191 19.9516 5.65872 19.918 5.7428L19.4303 6.76855C19.4051 6.81059 19.4135 6.86944 19.4471 6.90307L19.4639 6.91989C20.187 7.55047 20.6831 7.87837 21.6584 7.87837C22.3058 7.87837 22.7262 7.73544 23.1634 7.45799C23.1802 7.45799 23.197 7.43276 23.2054 7.41595Z'
				fill='white'
			/>
			<path
				d='M5.38938 1.48006L5.80137 0.395452C5.85181 0.277743 5.76773 0.160034 5.65003 0.160034H0.723069C0.655807 0.160034 0.596952 0.202073 0.571729 0.269335L0.00840778 1.64821C0.00840778 1.64821 0 1.68184 0 1.69866V7.64296C0 7.81952 0.07567 7.89519 0.168156 7.89519H1.47136C1.56385 7.89519 1.63952 7.81952 1.63952 7.72703V4.92724C1.63952 4.83476 1.71519 4.75909 1.80767 4.75909H3.97688C4.04414 4.75909 4.1114 4.71705 4.13663 4.64979L4.57383 3.57359C4.61587 3.46429 4.5402 3.34658 4.41408 3.34658H1.80767C1.71519 3.34658 1.63952 3.27091 1.63952 3.17843V1.7491C1.63952 1.65662 1.71519 1.58095 1.80767 1.58095H5.23804C5.30531 1.58095 5.36416 1.53891 5.38938 1.48006Z'
				fill='white'
			/>
			<path
				d='M19.1025 1.48006L19.5734 0.395452C19.6238 0.286151 19.5397 0.160034 19.422 0.160034H14.823C14.7557 0.160034 14.6884 0.202073 14.6632 0.269335L14.0663 1.75751C14.0579 1.77433 14.0579 1.79955 14.0579 1.81637V7.71863C14.0579 7.81111 14.1335 7.88678 14.226 7.88678H17.6059C17.6732 7.88678 17.7321 7.84474 17.7657 7.78589L18.2113 6.70129C18.2533 6.59198 18.1777 6.46587 18.0516 6.46587H15.7899C15.6974 6.46587 15.6217 6.3902 15.6217 6.29771V4.88521C15.6217 4.79272 15.6974 4.71705 15.7899 4.71705H17.6984C17.7657 4.71705 17.8245 4.67501 17.8498 4.61616L18.2954 3.53996C18.3374 3.43066 18.2617 3.30454 18.144 3.30454H15.7899C15.6974 3.30454 15.6217 3.22887 15.6217 3.13639V1.7491C15.6217 1.65662 15.6974 1.58095 15.7899 1.58095H18.9596C19.01 1.58095 19.0689 1.54732 19.1025 1.48006Z'
				fill='white'
			/>
			<path
				d='M12.5865 7.3823L11.3085 5.48214C11.2496 5.39806 11.2833 5.28876 11.3674 5.23832C12.2081 4.77589 12.6706 3.94352 12.6706 2.8421C12.6706 1.13532 11.5187 0.151611 9.51765 0.151611H7.06257C6.99531 0.151611 6.92805 0.19365 6.90283 0.260912L6.43199 1.53889C6.42358 1.55571 6.42358 1.58093 6.42358 1.59775V7.71861C6.42358 7.8111 6.49925 7.88677 6.59174 7.88677H7.81087C7.90335 7.88677 7.97902 7.8111 7.97902 7.71861V5.81845C7.97902 5.72597 8.05469 5.6503 8.14718 5.6503H9.50924C9.56809 5.6503 9.61854 5.6503 9.67739 5.6503C9.73625 5.6503 9.7951 5.68393 9.82033 5.73438L11.0815 7.8111C11.1151 7.86154 11.1656 7.89517 11.2244 7.89517H12.4436C12.536 7.89517 12.6117 7.8195 12.6117 7.72702V7.47479C12.6117 7.44115 12.6033 7.40752 12.5865 7.3823ZM9.50924 4.22938H8.14718C8.05469 4.22938 7.97902 4.15371 7.97902 4.06123V1.74909C7.97902 1.6566 8.05469 1.58093 8.14718 1.58093H9.50924C10.6107 1.58093 11.1656 2.01814 11.1656 2.87573C11.174 3.77536 10.6107 4.22938 9.50924 4.22938Z'
				fill='white'
			/>
			<path
				d='M31.9832 6.33134L29.2927 0.260928C29.2675 0.202073 29.2087 0.160034 29.1414 0.160034H29.1246H27.8634C27.7961 0.160034 27.7373 0.202073 27.7121 0.260928L24.4414 7.65136C24.391 7.76066 24.4751 7.88678 24.5928 7.88678H25.8624C25.9296 7.88678 25.9885 7.84474 26.0137 7.78589L26.6359 6.31453C26.6611 6.25567 26.72 6.21363 26.7872 6.21363H30.1251C30.184 6.21363 30.226 6.23886 30.2596 6.2893L30.8482 7.72703L30.8818 7.8027L30.907 7.85315C30.9238 7.87837 30.9323 7.88678 30.9491 7.89519C30.9659 7.89519 30.9827 7.89519 31.0079 7.89519H31.2938C31.361 7.89519 31.4199 7.85315 31.4451 7.7943L31.9748 6.48268C32.0084 6.43224 32.0084 6.38179 31.9832 6.33134ZM29.3768 4.80113H27.5271C27.4094 4.80113 27.3253 4.67501 27.3758 4.56571L28.3006 2.3965C28.3595 2.26198 28.5528 2.26198 28.6117 2.3965L29.5366 4.56571C29.587 4.67501 29.5029 4.80113 29.3768 4.80113Z'
				fill='white'
			/>
		</svg>
	);
}

function SithsLogo(): JSX.Element {
	return (
		<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'>
			<path
				fillRule='evenodd'
				clipRule='evenodd'
				d='M11.2589 29.8871C11.2589 31.0542 10.2783 32.0001 9.06891 32.0001C7.85888 32.0001 6.87892 31.0542 6.87892 29.8871V28.9133H2.6835C1.46609 28.9133 0.47998 27.9616 0.47998 26.7879C0.47998 25.629 1.45379 24.6891 2.65582 24.6891H6.87892V16.313L6.20409 16.0846C2.0911 14.6884 -0.40032 10.4464 0.769108 6.13787C1.63588 2.94374 4.48593 0.519804 7.87857 0.0777405C13.591 -0.666943 18.4231 4.04682 17.5563 9.57053C17.0771 12.6264 14.7635 15.1227 11.7412 16.1493L11.2589 16.313V29.8871ZM4.93808 7.48898C4.37274 10.3449 6.93429 12.8139 9.89446 12.268C11.5486 11.963 12.8829 10.6754 13.1991 9.07922C13.7645 6.22391 11.2035 3.75488 8.24336 4.30078C6.58857 4.60578 5.25427 5.8928 4.93808 7.48898Z'
				fill='#00706E'
			/>
			<path
				d='M19.8796 6.04179C19.8067 4.58719 19.4815 3.202 18.9324 1.93391C28.3961 3.02033 35.0698 13.6298 29.4594 24.109C27.2517 28.2344 22.9339 30.7295 18.3223 30.7295H15.4718C14.4251 30.7295 13.5762 29.864 13.5762 28.7975C13.5762 27.7304 14.4251 26.8661 15.4718 26.8661H18.3181C21.4304 26.8661 24.3502 25.2322 25.9431 22.5083C30.2313 15.175 26.1925 7.67021 19.8796 6.04179Z'
				fill='#00706E'
			/>
		</svg>
	);
}

function ClientPwdLogo(): JSX.Element {
	return (
		<svg width='32' height='32' viewBox='0 0 32 32' fill='none'>
			<path
				d='M5 10C4.44772 10 4 10.4477 4 11V20C4 20.5523 4.44772 21 5 21H17C17.5523 21 18 21.4477 18 22C18 22.5523 17.5523 23 17 23H5C3.34315 23 2 21.6569 2 20V11C2 9.34315 3.34315 8 5 8H25C26.6569 8 28 9.34315 28 11V12.5C28 13.0523 27.5523 13.5 27 13.5C26.4477 13.5 26 13.0523 26 12.5V11C26 10.4477 25.5523 10 25 10H5Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				d='M25.5089 23C25.5089 23.5523 25.0612 24 24.5089 24C23.9566 24 23.5089 23.5523 23.5089 23C23.5089 22.4477 23.9566 22 24.5089 22C25.0612 22 25.5089 22.4477 25.5089 23Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				fill-rule='evenodd'
				clip-rule='evenodd'
				d='M19 20C19 18.8954 19.8954 18 21 18V17.5C21 15.567 22.567 14 24.5 14C26.433 14 28 15.567 28 17.5V18C29.1046 18 30 18.8954 30 20V26C30 27.1046 29.1046 28 28 28H21C19.8954 28 19 27.1046 19 26V20ZM26 18V17.5C26 16.6716 25.3284 16 24.5 16C23.6716 16 23 16.6716 23 17.5V18H26ZM28 20H21V26H28V20Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				d='M7.5 17C8.32843 17 9 16.3284 9 15.5C9 14.6716 8.32843 14 7.5 14C6.67157 14 6 14.6716 6 15.5C6 16.3284 6.67157 17 7.5 17Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				d='M14 15.5C14 16.3284 13.3284 17 12.5 17C11.6716 17 11 16.3284 11 15.5C11 14.6716 11.6716 14 12.5 14C13.3284 14 14 14.6716 14 15.5Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				d='M17.5 17C18.3284 17 19 16.3284 19 15.5C19 14.6716 18.3284 14 17.5 14C16.6716 14 16 14.6716 16 15.5C16 16.3284 16.6716 17 17.5 17Z'
				fill={tokens.color.content.mutedBrand}
			/>
		</svg>
	);
}

function SmsLogo(): JSX.Element {
	return (
		<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'>
			<path
				d='M10.5 6C10.5 5.44772 10.9477 5 11.5 5H14.5C15.0523 5 15.5 5.44772 15.5 6C15.5 6.55228 15.0523 7 14.5 7H11.5C10.9477 7 10.5 6.55228 10.5 6Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				d='M13 24C12.1716 24 11.5 24.6716 11.5 25.5C11.5 26.3284 12.1716 27 13 27C13.8284 27 14.5 26.3284 14.5 25.5C14.5 24.6716 13.8284 24 13 24Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				d='M17.5 15.9998C18.0523 15.9998 18.5 15.552 18.5 14.9998C18.5 14.4475 18.0523 13.9998 17.5 13.9998C16.9478 13.9998 16.5 14.4475 16.5 14.9998C16.5 15.552 16.9478 15.9998 17.5 15.9998Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				d='M22.5 15C22.5 15.5523 22.0523 16 21.5 16C20.9477 16 20.5 15.5523 20.5 15C20.5 14.4477 20.9477 14 21.5 14C22.0523 14 22.5 14.4477 22.5 15Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				d='M25.5 16C26.0523 16 26.5 15.5523 26.5 15C26.5 14.4477 26.0523 14 25.5 14C24.9477 14 24.5 14.4477 24.5 15C24.5 15.5523 24.9477 16 25.5 16Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				fillRule='evenodd'
				clipRule='evenodd'
				d='M7 2C5.34315 2 4 3.34315 4 5V27C4 28.6569 5.34315 30 7 30H19C20.6569 30 22 28.6569 22 27V21.25H27C28.6569 21.25 30 19.9069 30 18.25V11C30 9.34315 28.6569 8 27 8H22V5C22 3.34315 20.6569 2 19 2H7ZM18.4872 24.6757L20 23.0253V27C20 27.5523 19.5523 28 19 28H7C6.44772 28 6 27.5523 6 27V5C6 4.44772 6.44772 4 7 4H19C19.5523 4 20 4.44772 20 5V8H16C14.3431 8 13 9.34315 13 11V18.25C13 19.9069 14.3431 21.25 16 21.25H16.75V24C16.75 24.4124 17.0031 24.7825 17.3875 24.932C17.7718 25.0815 18.2085 24.9797 18.4872 24.6757ZM16 10C15.4477 10 15 10.4477 15 11V18.25C15 18.8023 15.4477 19.25 16 19.25H17.75C18.3023 19.25 18.75 19.6977 18.75 20.25V21.4292L20.4503 19.5743C20.6398 19.3676 20.9072 19.25 21.1875 19.25H27C27.5523 19.25 28 18.8023 28 18.25V11C28 10.4477 27.5523 10 27 10H16Z'
				fill={tokens.color.content.mutedBrand}
			/>
		</svg>
	);
}

function BankIdLogo(): JSX.Element {
	return (
		<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'>
			<path
				d='M10.0557 16.6155L11.3924 8.19074C10.8658 8.19074 9.96453 8.19074 9.96453 8.19074C9.29618 8.19074 8.43542 7.81608 8.18225 7.12752C8.10124 6.89463 7.90884 6.09468 9.01263 5.31499C9.40757 5.04159 9.66073 4.73781 9.71137 4.50492C9.762 4.26189 9.70124 4.04925 9.52909 3.88724C9.28605 3.65434 8.8101 3.52271 8.20251 3.52271C7.17972 3.52271 6.46073 4.11001 6.38985 4.53529C6.33922 4.8492 6.58225 5.10234 6.79491 5.26436C7.43289 5.74027 7.58478 6.42883 7.18985 7.07689C6.78478 7.7452 5.90377 8.18061 4.962 8.19074C4.962 8.19074 4.03035 8.19074 3.50377 8.19074C3.38225 9.01094 1.39744 21.5873 1.24554 22.5796H9.12403C9.19491 22.1442 9.55947 19.7646 10.0557 16.6155Z'
				fill='white'
			/>
			<path
				d='M0.820232 25.4961H4.05061C5.42783 25.4961 5.762 26.1948 5.66074 26.8327C5.57972 27.3491 5.2253 27.7339 4.6177 27.9871C5.38732 28.2807 5.69112 28.7364 5.57973 29.4553C5.43795 30.3565 4.65821 31.0248 3.63542 31.0248H-0.0405273L0.820232 25.4961ZM2.95694 27.7845C3.58479 27.7845 3.87846 27.4504 3.93922 27.0555C3.99998 26.6302 3.80757 26.3365 3.17972 26.3365H2.62276L2.39998 27.7845H2.95694ZM2.61264 30.1641C3.26074 30.1641 3.63542 29.9009 3.72656 29.3642C3.79745 28.8984 3.53416 28.625 2.90631 28.625H2.27846L2.03542 30.1743H2.61264V30.1641Z'
				fill='white'
			/>
			<path
				d='M10.1064 31.0653C9.2659 31.126 8.86083 31.0349 8.6583 30.6704C8.21273 30.9438 7.71653 31.0855 7.18995 31.0855C6.23805 31.0855 5.90387 30.5894 5.99501 30.0426C6.03552 29.7793 6.18742 29.5262 6.43045 29.3135C6.95704 28.8578 8.25324 28.7971 8.75957 28.4528C8.80007 28.068 8.64817 27.9263 8.17222 27.9263C7.61526 27.9263 7.14944 28.1085 6.34944 28.6553L6.54185 27.3997C7.23045 26.9035 7.89881 26.6707 8.66843 26.6707C9.65071 26.6707 10.5216 27.0757 10.3596 28.149L10.1672 29.3641C10.0963 29.7894 10.1165 29.9211 10.5925 29.9312L10.1064 31.0653ZM8.64817 29.1515C8.2026 29.435 7.37222 29.3844 7.28109 29.9717C7.24058 30.2451 7.41273 30.4476 7.68615 30.4476C7.94944 30.4476 8.27349 30.3362 8.53678 30.154C8.51653 30.0527 8.52666 29.9514 8.55704 29.7591L8.64817 29.1515Z'
				fill='white'
			/>
			<path
				d='M11.676 26.7415H13.357L13.2659 27.2985C13.8026 26.8428 14.2076 26.6707 14.7342 26.6707C15.676 26.6707 16.1114 27.2478 15.9595 28.1895L15.5241 31.0147H13.8431L14.2076 28.6756C14.2785 28.2503 14.1469 28.0478 13.8228 28.0478C13.5595 28.0478 13.3165 28.1895 13.0836 28.5034L12.6988 31.0045H11.0178L11.676 26.7415Z'
				fill='white'
			/>
			<path
				d='M17.2759 25.4961H18.9569L18.5316 28.2098L20.1417 26.7416H22.2177L20.1519 28.5642L21.8126 31.0147H19.6962L18.4202 29.03H18.4L18.0962 31.0046H16.4152L17.2759 25.4961Z'
				fill='white'
			/>
			<path d='M23.2202 25.4961H25.1544L24.3037 31.0147H22.3696L23.2202 25.4961Z' fill='white' />
			<path
				d='M26.0962 25.4961H28.8607C30.9974 25.4961 31.6152 27.0454 31.4126 28.3313C31.2202 29.587 30.2278 31.0147 28.3544 31.0147H25.2354L26.0962 25.4961ZM27.8886 29.6983C28.8303 29.6983 29.3468 29.2325 29.4987 28.2503C29.6101 27.5213 29.3873 26.8023 28.3443 26.8023H27.8278L27.3822 29.6983H27.8886Z'
				fill='white'
			/>
			<path
				d='M20.648 0.829346H12.5974L11.524 7.64407H12.8911C13.6404 7.64407 14.3493 7.29979 14.6531 6.80362C14.7544 6.64161 14.7949 6.49984 14.7949 6.36821C14.7949 6.08468 14.6025 5.87204 14.4101 5.73028C13.8835 5.33537 13.7721 4.92021 13.7721 4.62655C13.7721 4.5658 13.7721 4.51517 13.7822 4.46454C13.8936 3.7456 14.8658 2.96591 16.1518 2.96591C16.9215 2.96591 17.5088 3.14817 17.8632 3.48233C18.1772 3.77598 18.2987 4.19114 18.2075 4.62655C18.0961 5.14297 17.5797 5.56826 17.286 5.78091C16.5063 6.3277 16.6075 6.80362 16.6582 6.94538C16.8202 7.37067 17.4379 7.64407 17.9139 7.64407H19.9999C19.9999 7.64407 19.9999 7.64407 19.9999 7.6542C22.8354 7.67445 24.3544 8.98069 23.8784 12.0185C23.4328 14.8436 21.2658 16.0587 18.6835 16.0789L17.6607 22.6H19.1696C25.5392 22.6 30.7442 18.5092 31.767 12.0488C33.0329 4.02913 27.9392 0.829346 20.648 0.829346Z'
				fill='white'
			/>
		</svg>
	);
}

function PadLockIcon() {
	return (
		<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'>
			<path
				fillRule='evenodd'
				clipRule='evenodd'
				d='M9 10C6.23858 10 4 12.2386 4 15V25C4 27.7614 6.23858 30 9 30H9.5C10.0523 30 10.5 29.5523 10.5 29C10.5 28.4477 10.0523 28 9.5 28H9C7.34315 28 6 26.6569 6 25V15C6 13.3431 7.34315 12 9 12H23C24.6569 12 26 13.3431 26 15V20.5C26 21.0523 26.4477 21.5 27 21.5C27.5523 21.5 28 21.0523 28 20.5V15C28 12.2386 25.7614 10 23 10V9C23 7.14347 22.2625 5.36302 20.9497 4.05028C19.637 2.73753 17.8565 2 16 2C14.1435 2 12.363 2.73753 11.0502 4.05028C9.7375 5.36303 9 7.14348 9 9V10ZM12.4645 5.46449C13.4022 4.5268 14.6739 4 16 4C17.3261 4 18.5978 4.5268 19.5355 5.46449C20.4732 6.40218 21 7.67393 21 9V10H11V9C11 7.67392 11.5268 6.40218 12.4645 5.46449Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				fillRule='evenodd'
				clipRule='evenodd'
				d='M16 15C14.3431 15 13 16.3431 13 18C13 19.3113 13.8414 20.4262 15.0137 20.8341C15.0047 20.8881 15 20.9435 15 21V23C15 23.5523 15.4477 24 16 24C16.5523 24 17 23.5523 17 23V21C17 20.9435 16.9953 20.8881 16.9863 20.8341C18.1586 20.4262 19 19.3113 19 18C19 16.3431 17.6569 15 16 15ZM15 18C15 17.4477 15.4477 17 16 17C16.5523 17 17 17.4477 17 18C17 18.5523 16.5523 19 16 19C15.4477 19 15 18.5523 15 18Z'
				fill={tokens.color.content.mutedBrand}
			/>
			<path
				fillRule='evenodd'
				clipRule='evenodd'
				d='M24.1707 28C24.5825 29.1652 25.6938 30 27 30C28.6569 30 30 28.6569 30 27C30 25.3431 28.6569 24 27 24C25.6938 24 24.5825 24.8348 24.1707 26H16C15.4477 26 15 26.4477 15 27C15 27.5523 15.4477 28 16 28H17V29C17 29.5523 17.4477 30 18 30C18.5523 30 19 29.5523 19 29V28H20V29C20 29.5523 20.4477 30 21 30C21.5523 30 22 29.5523 22 29V28H24.1707ZM27 26C26.4477 26 26 26.4477 26 27C26 27.5523 26.4477 28 27 28C27.5523 28 28 27.5523 28 27C28 26.4477 27.5523 26 27 26Z'
				fill={tokens.color.content.mutedBrand}
			/>
		</svg>
	);
}
